/**
 * Serializes an I-JSON-compatible value according to RFC 8785 (JCS).
 *
 * JSON.stringify supplies the required ECMAScript string and number encoding;
 * object keys are then sorted by UTF-16 code units before recursive emission.
 */
function assertValidUnicode(value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit >= 0xd800 && codeUnit <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (index + 1 >= value.length || next < 0xdc00 || next > 0xdfff) {
        throw new TypeError("RFC 8785 canonical JSON cannot contain an unpaired UTF-16 surrogate");
      }
      index += 1;
    } else if (codeUnit >= 0xdc00 && codeUnit <= 0xdfff) {
      throw new TypeError("RFC 8785 canonical JSON cannot contain an unpaired UTF-16 surrogate");
    }
  }
}

export function canonicalizeJson(value: unknown): string {
  if (value === null) return "null";

  switch (typeof value) {
    case "boolean":
      return JSON.stringify(value);
    case "string":
      assertValidUnicode(value);
      return JSON.stringify(value);
    case "number":
      if (!Number.isFinite(value)) {
        throw new TypeError("RFC 8785 canonical JSON cannot contain a non-finite number");
      }
      return JSON.stringify(value);
    case "object": {
      if (Array.isArray(value)) {
        const items: string[] = [];
        for (let index = 0; index < value.length; index += 1) {
          if (!Object.prototype.hasOwnProperty.call(value, index)) {
            throw new TypeError("RFC 8785 canonical JSON cannot contain a sparse array");
          }
          items.push(canonicalizeJson(value[index]));
        }
        return `[${items.join(",")}]`;
      }

      const prototype = Object.getPrototypeOf(value);
      if (prototype !== Object.prototype && prototype !== null) {
        throw new TypeError("RFC 8785 canonical JSON requires a plain object");
      }
      const record = value as Record<string, unknown>;
      const members = Object.keys(record)
        .sort()
        .map((key) => {
          assertValidUnicode(key);
          return `${JSON.stringify(key)}:${canonicalizeJson(record[key])}`;
        });
      return `{${members.join(",")}}`;
    }
    default:
      throw new TypeError(`RFC 8785 canonical JSON cannot contain ${typeof value}`);
  }
}
